/**
 * HENDELSESLAGET (grep C1, QA13 punkt 55, 63, 72, 73, 77, 80, 81) — én kilde for «hva skjer når».
 *
 * ---------------------------------------------------------------------------------------------
 * SJU LESINGER BLE ÉN.
 *
 * Tidslinja på en person, tidslinja på en ressurs, timeplanen på programposten, konfliktene,
 * eksporten og Mine vakter leste hver sin bit. Bandet så aldri sitt eget sett, flyet fantes ikke
 * for tidslinja, og eksporten av en scene var tom. Nå leser alle det samme: `app.edition_events` og
 * `app.edition_event_people` (0924), og dette er formen svaret har på tråden.
 *
 * ---------------------------------------------------------------------------------------------
 * PERSONENS EGEN TID ER EN EGEN TID.
 *
 * `people[].startsAt/endsAt` er tiden for DEN personen: passasjeren har reisens 10:30–14:00,
 * sjåføren bilens 06:00–15:00 (253.30). Hendelsens egen tid står på hendelsen. En tidslinje for en
 * person tegner personens tid; en tidslinje for en ressurs tegner hendelsens.
 *
 * ---------------------------------------------------------------------------------------------
 * LAGET ER EN FUNKSJON AV TYPEN, OG LIGGER HER.
 *
 * Tidslinja grupperer i faste lag (analysen av 63 + 80), og eksporten bruker de samme. Én tabell
 * her framfor én i hver leser, så «middag hører til Mat» ikke kan bli to svar.
 */
import { z } from 'zod';

import { travelModeSchema } from '../enums.js';

export const EDITION_EVENT_KINDS = [
  'set',
  'stage_time',
  'travel',
  'local_transport',
  'stay',
  'dinner',
  'shift',
  'allocation',
  'opening',
  'task',
  'event',
  'deadline',
] as const;
export const editionEventKindSchema = z.enum(EDITION_EVENT_KINDS);
export type EditionEventKind = z.infer<typeof editionEventKindSchema>;

/** Rollen en person har på en hendelse. Passasjer og sjåfør på samme tur er to roller. */
export const EVENT_PERSON_ROLES = [
  'passenger',
  'driver',
  'room_guest',
  'diner',
  'shift',
  'artist_party',
  'participant',
] as const;
export const eventPersonRoleSchema = z.enum(EVENT_PERSON_ROLES);
export type EventPersonRole = z.infer<typeof eventPersonRoleSchema>;

/**
 * LAGENE, i fast rekkefølge (63 + 80): Program, Reise og transport, Overnatting, Mat, Vakter,
 * Reservasjoner — og Oppgaver og kalender, som er av som standard (253.31). Åpningstida er ikke et
 * lag: den er et bakgrunnsbånd på ressursens tidslinje.
 */
export const TIMELINE_LAYERS = [
  'programme',
  'travel',
  'stay',
  'food',
  'shifts',
  'reservations',
  'calendar',
] as const;
export const timelineLayerSchema = z.enum(TIMELINE_LAYERS);
export type TimelineLayer = z.infer<typeof timelineLayerSchema>;

/** Laget hver type hører til. `opening` har ingen: den er bakgrunn. */
export const LAYER_OF_KIND: Readonly<Record<EditionEventKind, TimelineLayer | null>> = {
  set: 'programme',
  stage_time: 'programme',
  travel: 'travel',
  local_transport: 'travel',
  stay: 'stay',
  dinner: 'food',
  shift: 'shifts',
  allocation: 'reservations',
  opening: null,
  task: 'calendar',
  event: 'calendar',
  deadline: 'calendar',
};

/** Lagene som er på når tidslinja åpnes. Oppgaver og kalender er av (253.31). */
export const DEFAULT_TIMELINE_LAYERS: readonly TimelineLayer[] = [
  'programme',
  'travel',
  'stay',
  'food',
  'shifts',
  'reservations',
];

/** Varslene en hendelse kan bære — de samme kodene som timeplanen (`TIMETABLE_WARNINGS`). */
export const EDITION_EVENT_WARNINGS = ['missingDriver', 'missingPax', 'missingStage'] as const;

export const editionEventPersonSchema = z
  .object({
    personId: z.uuid(),
    /** Null når personen ikke har en kontakt i huset som spør — raden står likevel. */
    name: z.string().nullable(),
    role: eventPersonRoleSchema,
    /** PERSONENS tid — se filhodet. */
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    /** «314» for romgjesten. */
    detail: z.string().nullable(),
  })
  .meta({ id: 'EditionEventPerson' });
export type EditionEventPerson = z.infer<typeof editionEventPersonSchema>;

export const editionEventSchema = z
  .object({
    /** «stay:<id>:2027-07-15». Én kilderad kan gi flere hendelser; nøkkelen skiller dem. */
    key: z.string(),
    kind: editionEventKindSchema,
    /** Underdelen: `night`/`check_in`/`check_out` på et opphold, `start`/`due` på en oppgave … */
    slot: z.string(),
    /** Raden hendelsen leses fra — reisen, vakta, programposten, reservasjonen. */
    sourceId: z.uuid(),
    /** Vakta en reservasjon er bestilt til (55). Null for alt annet. */
    parentId: z.uuid().nullable(),
    layer: timelineLayerSchema.nullable(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    /** Aldri tom: et tidspunkt har `endsAt === startsAt`. */
    endsAt: z.iso.datetime(),
    dateOnly: z.boolean(),
    /** Satt på en natt. Den hører til natt-stripa, ikke til timelinja. */
    night: z.iso.date().nullable(),
    resourceId: z.uuid().nullable(),
    resourceName: z.string().nullable(),
    performanceId: z.uuid().nullable(),
    /** Acten, når hendelsen hører til en programpost: «Ari Bajgora». */
    actName: z.string().nullable(),
    departmentId: z.uuid().nullable(),
    status: z.string().nullable(),
    travelMode: travelModeSchema.nullable(),
    stageTimeKindId: z.uuid().nullable(),
    /** Scenetiden opptar scenen (253.29). Settet gjør det alltid. */
    occupiesStage: z.boolean(),
    /** Strek på scenen, ikke en rad på folk (Curfew, 253.28). */
    stageLine: z.boolean(),
    location: z.string().nullable(),
    warning: z.enum(EDITION_EVENT_WARNINGS).nullable(),
    people: z.array(editionEventPersonSchema),
  })
  .meta({ id: 'EditionEvent' });
export type EditionEvent = z.infer<typeof editionEventSchema>;

/**
 * Et vindu i én årgang, filtrert på én person ELLER én ressurs ELLER én programpost — eller ingen.
 * Ikke paginert: taket under er en påstand, som på den gamle tidslinja.
 */
export const listEditionEventsQuerySchema = z
  .object({
    editionId: z.uuid(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    personId: z.uuid().optional(),
    resourceId: z.uuid().optional(),
    performanceId: z.uuid().optional(),
  })
  .refine((value) => Date.parse(value.to) > Date.parse(value.from), {
    message: 'The end must come after the start.',
    path: ['to'],
  })
  .meta({ id: 'ListEditionEventsQuery' });
export type ListEditionEventsQuery = z.infer<typeof listEditionEventsQuerySchema>;

export const MAX_EDITION_EVENTS = 2000;

export const editionEventListResponseSchema = z
  .object({ items: z.array(editionEventSchema) })
  .meta({ id: 'EditionEventListResponse' });
export type EditionEventListResponse = z.infer<typeof editionEventListResponseSchema>;
